import { useEffect, useRef } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import type { ChatTurn, ToolEvent } from "@/hooks/useChat";
import { Markdown } from "@/lib/markdown";
import { SourceList } from "./SourceList";
import { ActivityLog } from "./ActivityLog";
import { MessageActions } from "./MessageActions";
import { FollowUpChips } from "./FollowUpChips";

interface MessageListProps {
  turns: ChatTurn[];
  toolEvents?: ToolEvent[];
  onRegenerate?: () => void;
  onFollowUp?: (question: string) => void;
  onCopyError?: (message: string) => void;
}

export function MessageList({ turns, toolEvents = [], onRegenerate, onFollowUp, onCopyError }: MessageListProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns]);

  const lastAssistantIdx = turns.reduce(
    (found, t, i) => (t.role === "assistant" && !t.pending && !t.error ? i : found),
    -1,
  );

  return (
    <div className="flex flex-col" aria-live="polite">
      {turns.map((turn, index) => (
        <Message
          key={turn.id}
          turn={turn}
          isLastAssistant={index === lastAssistantIdx}
          toolEvents={turn.pending ? toolEvents : []}
          question={index > 0 && turns[index - 1]?.role === "user" ? turns[index - 1].content : undefined}
          onRegenerate={onRegenerate}
          onFollowUp={onFollowUp}
          onCopyError={onCopyError}
        />
      ))}
      <div ref={endRef} aria-hidden />
    </div>
  );
}

interface MessageProps {
  turn: ChatTurn;
  isLastAssistant: boolean;
  toolEvents?: ToolEvent[];
  /** The user's question that preceded this assistant turn (for the bug report modal). */
  question?: string;
  onRegenerate?: () => void;
  onFollowUp?: (question: string) => void;
  onCopyError?: (message: string) => void;
}

function Message({ turn, isLastAssistant, toolEvents = [], question, onRegenerate, onFollowUp, onCopyError }: MessageProps) {
  const isUser = turn.role === "user";
  return (
    <article
      className={`mb-[22px] grid gap-3 ${
        isUser
          ? "grid-cols-[minmax(0,1fr)_32px] max-[680px]:grid-cols-[30px_minmax(0,1fr)]"
          : "grid-cols-[32px_minmax(0,1fr)] max-[680px]:grid-cols-[30px_minmax(0,1fr)]"
      }`}
    >
      <Avatar isUser={isUser} />
      <Bubble
        turn={turn}
        isLastAssistant={isLastAssistant}
        toolEvents={toolEvents}
        question={question}
        onRegenerate={onRegenerate}
        onFollowUp={onFollowUp}
        onCopyError={onCopyError}
      />
    </article>
  );
}

function Avatar({ isUser }: { isUser: boolean }) {
  return (
    <div
      className={
        isUser
          ? "col-start-2 grid h-8 w-8 place-items-center rounded-md bg-surface-muted border border-border text-[11px] font-semibold uppercase tracking-wider text-text-muted max-[680px]:col-start-1"
          : "grid h-8 w-8 place-items-center rounded-md bg-ndc-red text-[12px] font-bold text-white"
      }
      aria-hidden
    >
      {isUser ? "You" : "N"}
    </div>
  );
}

interface BubbleProps {
  turn: ChatTurn;
  isLastAssistant: boolean;
  toolEvents?: ToolEvent[];
  question?: string;
  onRegenerate?: () => void;
  onFollowUp?: (question: string) => void;
  onCopyError?: (message: string) => void;
}

function Bubble({ turn, isLastAssistant, toolEvents = [], question, onRegenerate, onFollowUp, onCopyError }: BubbleProps) {
  const isUser = turn.role === "user";

  if (isUser) {
    return (
      <div className="col-start-1 row-start-1 justify-self-end rounded-[14px_14px_4px_14px] border border-border bg-surface-muted px-[14px] py-[10px] text-[14.5px] leading-relaxed text-text max-[680px]:col-start-2 max-[680px]:row-start-auto max-[680px]:max-w-full max-[680px]:justify-self-stretch max-w-[min(620px,92%)]">
        {turn.content}
      </div>
    );
  }

  if (turn.pending && !turn.content) {
    return (
      <div className="prose-bubble">
        <ActivityLog events={toolEvents} />
      </div>
    );
  }

  // Error state: show the error message with an inline retry affordance.
  if (turn.error) {
    return (
      <div className="prose-bubble">
        <div className="flex items-start gap-2 rounded-lg border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-3 text-[13.5px]">
          <AlertCircle className="mt-[1px] h-4 w-4 shrink-0 text-[var(--danger)]" />
          <div className="flex-1">
            <span className="text-text">{turn.content}</span>
            {onRegenerate && isLastAssistant && (
              <button
                type="button"
                onClick={onRegenerate}
                className="ml-3 inline-flex items-center gap-1 text-[12.5px] font-medium text-ndc-red hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ndc-red"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const sourceListId = `ndc-src-${turn.id}`;
  return (
    <div className="prose-bubble">
      <Markdown
        text={turn.content}
        sources={turn.sources ?? []}
        sourceListId={sourceListId}
      />
      {turn.sources && turn.sources.length > 0 ? (
        <SourceList sources={turn.sources} idPrefix={sourceListId} />
      ) : null}
      {!turn.error && turn.content ? (
        <MessageActions
          text={turn.content}
          messageId={turn.id}
          requestId={turn.requestId ?? null}
          isLast={isLastAssistant}
          question={question}
          onRegenerate={onRegenerate}
          onCopyError={onCopyError}
        />
      ) : null}
      {/* Follow-up suggestions only on the last completed assistant turn */}
      {isLastAssistant && turn.followUpQuestions && turn.followUpQuestions.length > 0 && onFollowUp ? (
        <FollowUpChips questions={turn.followUpQuestions} onPick={onFollowUp} />
      ) : null}
    </div>
  );
}
